import type { InvestmentContributionResponse, ReportHorizon } from "@eu.mdotm.api/sphere";
import {
	type OrderBy,
	type TableColumn,
	type TableWithGroupsProps,
	AutoSortTable,
	AutoSortTableWithGroups,
	Column,
	Row,
	TableDataCell,
	Text,
} from "@mdotm/shadcn/legacy/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { builtInSort, builtInSortFnFor, type Setter } from "@mdotm/shadcn/legacy/utils";
import { DropdownMenu, Select, Switch } from "@mdotm/shadcn/mdotm/components";
import { ForEach, useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { runWithErrorReporting } from "$root/api/error-reporting";
import { WidgetDownloadTrigger } from "$root/components/DropdownMenuTrigger";
import GraphLegend from "$root/components/GraphLegend";
import { IconWalls } from "$root/components/IconWall";
import ColoredRectangle from "$root/components/icons/ColoredRectangle";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { workspaceRepository } from "$root/functional-areas/workspace/repository";
import { useLocaleFormatters } from "$root/localization/hooks";
import { asOfMissingDataWall } from "$root/pages/PortfolioDetails/as-of/missingDataWall";
import {
	PortfolioQueryWidgetBase,
	portfolioWidgetMissingDataReason,
	WidgetStatus,
} from "$root/pages/PortfolioDetails/PortfolioWidgetStatus";
import { useMappedQueries } from "$root/third-party-integrations/react-query/hooks";
import { BarGraphPCSvg } from "$root/ui-lib/charts";
import {
	type ContextContent,
	DrillDownByValueMap,
	downloadContentDisposition,
	getGraphMarkers,
	objSumRounded,
	qualifier,
	roundCustomByStep,
	withContext,
} from "$root/utils";
import { PortfolioContext } from "$root/widgets-architecture/contexts/portfolio";
import { useWidgetOptions } from "$root/widgets-architecture/layout/WidgetsMapper/context";
import { horizonMap, horizonOptions } from "../const";
import { buildWidgetExportConfig, type ContributionAggregation } from "../widget-export";
import { isAsOfReadable } from "$root/pages/PortfolioDetails/as-of/useAsOfSnapshot";
import { useQuery } from "@tanstack/react-query";

const groupedCategoryOptions = [
	{
		label: "Macro Asset Class vs Micro Asset Class",
		selector: "assetClass",
		vsSelector: "microAssetClass",
	},
	{
		label: "Macro Asset Class vs Geography",
		selector: "assetClass",
		vsSelector: "geography",
	},
	{
		label: "Micro Asset Class vs Geography",
		selector: "microAssetClass",
		vsSelector: "geography",
	},
	{
		label: "Geography vs Macro Asset Class",
		selector: "geography",
		vsSelector: "assetClass",
	},
	{
		label: "Geography vs Micro Asset Class",
		selector: "geography",
		vsSelector: "microAssetClass",
	},
];

type CategorySelection = { kind: "grouped"; selector: string; vsSelector: string } | { kind: "composition" };

const defaultCategorySelection: CategorySelection = {
	kind: "grouped",
	selector: groupedCategoryOptions[0].selector,
	vsSelector: groupedCategoryOptions[0].vsSelector,
};

const categorySelectOptions: Array<{
	label: string;
	value: CategorySelection;
}> = [
	...groupedCategoryOptions.map((opt) => ({
		label: opt.label,
		value: {
			kind: "grouped" as const,
			selector: opt.selector,
			vsSelector: opt.vsSelector,
		},
	})),
	{ label: "Composition", value: { kind: "composition" } },
];

/** Category selector → token used to build the export's `aggregation` enum (e.g. `MACRO_VS_GEOGRAPHY`). */
const AGGREGATION_TOKEN_BY_SELECTOR: Record<string, string> = {
	assetClass: "MACRO",
	microAssetClass: "MICRO",
	geography: "GEOGRAPHY",
};

/**
 * Builds the export `aggregation` enum from the widget's current category selection.
 * @internal — exported for tests
 */
export function categorySelectionToAggregation(category: CategorySelection): ContributionAggregation {
	if (category.kind === "composition") {
		return "COMPOSITION";
	}
	return `${AGGREGATION_TOKEN_BY_SELECTOR[category.selector]}_VS_${AGGREGATION_TOKEN_BY_SELECTOR[category.vsSelector]}` as ContributionAggregation;
}

/**
 * Merges the per-instrument volatility and performance contributions into one flat row per ticker,
 * preferring the display `name` over the raw ticker, and (when `significantOnly`) dropping rows whose
 * both metrics round below the 0.01 significance floor.
 * @internal — exported for tests
 */
export function buildCompositionRows(
	realizedVolatilityContribution: InvestmentContributionResponse,
	realizedPerformanceContribution: InvestmentContributionResponse,
	significantOnly: boolean,
): Array<{
	index: number;
	value: string;
	computedData: {
		performanceValue: number | null;
		volatilityValue: number | null;
	};
}> {
	const vol = realizedVolatilityContribution.currentOnInstruments ?? [];
	const perf = realizedPerformanceContribution.currentOnInstruments ?? [];
	const byTickerName = new Map<string, { name?: string; volatilityValue?: number; performanceValue?: number }>();
	for (const el of vol) {
		if (!el.tickerName) continue;
		const row = byTickerName.get(el.tickerName) ?? {};
		row.volatilityValue = el.value;
		row.name = el.name ?? row.name;
		byTickerName.set(el.tickerName, row);
	}
	for (const el of perf) {
		if (!el.tickerName) continue;
		const row = byTickerName.get(el.tickerName) ?? {};
		row.performanceValue = el.value;
		row.name = el.name ?? row.name;
		byTickerName.set(el.tickerName, row);
	}
	const rows = Array.from(byTickerName.entries()).map(([tickerName, row], index) => ({
		index,
		value: row.name ?? tickerName,
		computedData: {
			performanceValue: row.performanceValue ?? null,
			volatilityValue: row.volatilityValue ?? null,
		},
	}));
	if (!significantOnly) {
		return rows;
	}
	return rows.filter(
		(r) =>
			(r.computedData.volatilityValue != null && Math.abs(r.computedData.volatilityValue) >= 0.01) ||
			(r.computedData.performanceValue != null && Math.abs(r.computedData.performanceValue) >= 0.01),
	);
}

const PERFORMANCE_COLOR = "#005C8B";
const VOLATILITY_COLOR = "#572C7F";
const ContributionVolatility = (props: ContextContent<typeof PortfolioContext>) => {
	const { portfolio, selectedBenchmark, asOf, asOfSnapshot } = props;
	const uuid = props.portfolio?.uuid;
	const benchmarkId = selectedBenchmark ?? portfolio?.primaryBenchmarkIdentifier ?? "";

	const [category, setCategory] = useState<CategorySelection>(defaultCategorySelection);
	const [horizon, setHorizon] = useState<ReportHorizon>(horizonMap.ONE_MONTH.value);
	const [significantOnly, setSignificantOnly] = useState(true);
	// Mirror the table's column sort so the export reproduces it. Defaults to none (the provider's natural
	// order); once the user sorts a column the export carries that same sort. Shared by both table modes.
	const [orderBy, setOrderBy] = useState<OrderBy[]>([]);
	const { t } = useTranslation();

	const asOfReadable = isAsOfReadable(asOf, asOfSnapshot);
	const realizedVolatilityContributionQuery = useQuery({
		...portfolioRepository.volatilityContribution.getQueryConfig(uuid, {
			benchmarkId,
			horizon,
			asOf,
		}),
		enabled: asOfReadable,
	});
	const realizedPerformanceContributionQuery = useQuery({
		...portfolioRepository.performanceContribution.getQueryConfig(uuid, {
			benchmarkId,
			horizon,
			asOf,
		}),
		enabled: asOfReadable,
	});

	const query = useMappedQueries(
		([realizedVolatilityContribution, realizedPerformanceContribution]) => {
			if (!realizedPerformanceContribution?.current) {
				return {
					data: undefined,
					widgetStatus: portfolioWidgetMissingDataReason(props.portfolio, "ContributionVolatility"),
				};
			}
			return {
				data: {
					realizedVolatilityContribution: realizedVolatilityContribution || {
						current: [],
					},
					realizedPerformanceContribution,
				},
				widgetStatus: WidgetStatus.READY,
			};
		},
		[props.portfolio],
		[realizedVolatilityContributionQuery, realizedPerformanceContributionQuery],
	);

	// Download — mirrors the visible contribution table via the smart-workspace `investmentRealizedContribution`
	// data provider, exporting the currently-selected horizon.
	const exportContributionVolatility = async (investmentUuid: string, exportHorizon: ReportHorizon) => {
		const config = buildWidgetExportConfig(
			"ContributionVolatility",
			{
				investmentUuid,
				horizon: exportHorizon,
				aggregation: categorySelectionToAggregation(category),
				onlySignificant: significantOnly,
				sort: orderBy,
			},
			{ asOf },
		);
		await runWithErrorReporting(
			async () => {
				const file = await workspaceRepository.exportWorkspaceDataRaw.fetchQuery(config);
				await downloadContentDisposition(file);
			},
			{
				area: "portfolio",
				attemptedOperation: {
					message: "export performance and volatility contribution",
					payload: JSON.stringify(config),
				},
			},
		);
	};
	const exportContributionVolatilityRef = useUnsafeUpdatedRef(exportContributionVolatility);

	const canDownload = Boolean(uuid && query.data?.widgetStatus === WidgetStatus.READY);

	useWidgetOptions(
		() => ({
			title: t("PORTFOLIO_CONTRIBUTION_VOLATILITY.TITLE"),
			tooltip: t("PORTFOLIO_CONTRIBUTION_VOLATILITY.TOOLTIP"),
			actionHeader: canDownload ? (
				<DropdownMenu
					Trigger={WidgetDownloadTrigger}
					extraTriggerProps={{
						"data-qualifier": qualifier.widgets.portfolioContributionVolatility.downloadButton,
					}}
					actions={[
						{
							icon: "xls",
							onClickAsync: async () => {
								await exportContributionVolatilityRef.current(uuid, horizon);
							},
							label: t("PORTFOLIO_CONTRIBUTION_VOLATILITY.DOWNLOAD_TITLE"),
							"data-qualifier": qualifier.widgets.portfolioContributionVolatility.downloadItem,
						},
					]}
				/>
			) : null,
		}),
		[t, canDownload, uuid, horizon, exportContributionVolatilityRef],
	);

	return (
		<PortfolioQueryWidgetBase
			query={query}
			iconWalls={{
				waitingForHistoricalData: (
					<Column gap="0.75rem">
						<ContributionVolatilityHeader
							category={category}
							setCategory={setCategory}
							horizon={horizon}
							setHorizon={setHorizon}
							significantOnly={significantOnly}
						/>
						<IconWalls.WaitForHistoricalPortfolioWidgetData />
					</Column>
				),
			}}
		>
			{({ realizedPerformanceContribution, realizedVolatilityContribution }) => (
				<ContributionVolatilityInner
					ctx={props}
					realizedPerformanceContribution={realizedPerformanceContribution}
					realizedVolatilityContribution={realizedVolatilityContribution}
					onChangeCategory={setCategory}
					category={category}
					onChangeHorizon={setHorizon}
					horizon={horizon}
					significantOnly={significantOnly}
					onChangeSignificantOnly={setSignificantOnly}
					orderBy={orderBy}
					onOrderByChange={setOrderBy}
				/>
			)}
		</PortfolioQueryWidgetBase>
	);
};

/** @internal — exported for tests */
export const ContributionVolatilityInner = ({
	ctx,
	realizedPerformanceContribution,
	realizedVolatilityContribution,
	category,
	onChangeCategory,
	horizon,
	onChangeHorizon,
	significantOnly,
	onChangeSignificantOnly,
	orderBy,
	onOrderByChange,
}: {
	ctx: ContextContent<typeof PortfolioContext>;
	realizedVolatilityContribution: InvestmentContributionResponse;
	realizedPerformanceContribution: InvestmentContributionResponse;
	category: CategorySelection;
	onChangeCategory(opt: CategorySelection): void;
	horizon: ReportHorizon;
	onChangeHorizon(horizon: ReportHorizon): void;
	significantOnly: boolean;
	onChangeSignificantOnly(value: boolean): void;
	orderBy: OrderBy[];
	onOrderByChange: (orderBy: OrderBy[]) => void;
}) => {
	const { formatNumber } = useLocaleFormatters();
	const { t } = useTranslation();

	const contributionData = useMemo(() => {
		const aggregateData = {
			volatility: (realizedVolatilityContribution.current ?? []).map((el) => ({
				assetClass: el.assetClass,
				geography: el.geography,
				microAssetClass: el.microAssetClass,
				originalVolatility: el,
				volatilityValue: el.value,
				id: `${el.assetClass}${el.geography}${el.microAssetClass}`.split(" ").join("_"),
			})),

			performance: (realizedPerformanceContribution.current ?? []).map((el) => ({
				assetClass: el.assetClass,
				geography: el.geography,
				microAssetClass: el.microAssetClass,
				originalPerformance: el,
				performanceValue: el.value,
				id: `${el.assetClass}${el.geography}${el.microAssetClass}`.split(" ").join("_"),
			})),
		};

		const decupledByID = [
			...new Set([...aggregateData.performance.map((el) => el.id), ...aggregateData.volatility.map((el) => el.id)]),
		];

		const result = decupledByID.map((id) => {
			const currentVTK = aggregateData.volatility.find((el) => el.id === id);
			const currentPTK = aggregateData.performance.find((el) => el.id === id);

			return {
				...currentVTK,
				...currentPTK,
			};
		});
		return result;
	}, [realizedPerformanceContribution, realizedVolatilityContribution]);
	const computedContribution = useMemo(() => {
		if (category.kind !== "grouped") {
			return null;
		}
		const drilldown = DrillDownByValueMap(
			contributionData ?? [],
			category.selector as keyof (typeof contributionData)[number],
			category.vsSelector as keyof (typeof contributionData)[number],
			{
				volatilityValue: (val) => objSumRounded(val, "volatilityValue"),
				performanceValue: (val) => objSumRounded(val, "performanceValue"),
				maxVol: (val) => objSumRounded(val, "volatilityValue"),
				minVol: (val) => objSumRounded(val, "volatilityValue"),
				maxPer: (val) => objSumRounded(val, "performanceValue"),
				minPer: (val) => objSumRounded(val, "performanceValue"),
			},
			builtInSortFnFor("volatilityValue", "desc"),
		);

		if (!significantOnly) {
			return drilldown;
		}

		return {
			...drilldown,
			drilldownData: drilldown.drilldownData
				.filter(
					(x) =>
						(x.computedData.volatilityValue != null && Math.abs(x.computedData.volatilityValue) >= 0.01) ||
						(x.computedData.performanceValue != null && Math.abs(x.computedData.performanceValue) >= 0.01),
				)
				.map((x) => ({
					...x,
					drilldown: x.drilldown.filter(
						(y) =>
							(y.computedData.volatilityValue != null && Math.abs(y.computedData.volatilityValue) >= 0.01) ||
							(y.computedData.performanceValue != null && Math.abs(y.computedData.performanceValue) >= 0.01),
					),
				})),
		};
	}, [category, contributionData, significantOnly]);

	const compositionRows = useMemo(
		() => buildCompositionRows(realizedVolatilityContribution, realizedPerformanceContribution, significantOnly),
		[significantOnly, realizedPerformanceContribution, realizedVolatilityContribution],
	);

	// Correction Of Graph Scala
	const groupedGraphLimits = useMemo(() => {
		const value = (computedContribution?.drilldownData ?? []).reduce(
			(acc, group) => {
				const rowValues = group.drilldown.reduce(
					(a, row) => {
						return {
							maxVolatility: Math.max(a.maxVolatility, row.computedData.maxVol ?? 0, group.computedData.maxVol ?? 0),
							maxPerformance: Math.max(a.maxPerformance, row.computedData.maxPer ?? 0, group.computedData.maxPer ?? 0),
							minVolatility: Math.min(a.minVolatility, row.computedData.minVol ?? 0, group.computedData.minVol ?? 0),
							minPerformance: Math.min(a.minPerformance, row.computedData.minPer ?? 0, group.computedData.minPer ?? 0),
						};
					},
					{
						maxVolatility: 0,
						maxPerformance: 0,
						minVolatility: 0,
						minPerformance: 0,
					},
				);
				return {
					maxVolatility: Math.max(acc.maxVolatility, rowValues.maxVolatility),
					maxPerformance: Math.max(acc.maxPerformance, rowValues.maxPerformance),
					minVolatility: Math.min(acc.minVolatility, rowValues.minVolatility),
					minPerformance: Math.min(acc.minPerformance, rowValues.minPerformance),
				};
			},
			{
				maxVolatility: 0,
				maxPerformance: 0,
				minVolatility: 0,
				minPerformance: 0,
			},
		);
		return {
			maxVolatility: roundCustomByStep(value.maxVolatility, 0.5),
			maxPerformance: roundCustomByStep(value.maxPerformance, 0.5),
			minVolatility: roundCustomByStep(value.minVolatility, 0.5),
			minPerformance: roundCustomByStep(value.minPerformance, 0.5),
		};
	}, [computedContribution?.drilldownData]);

	const compositionGraphLimits = useMemo(() => {
		const value = compositionRows.reduce(
			(acc, row) => ({
				maxVolatility: Math.max(acc.maxVolatility, row.computedData.volatilityValue ?? 0),
				maxPerformance: Math.max(acc.maxPerformance, row.computedData.performanceValue ?? 0),
				minVolatility: Math.min(acc.minVolatility, row.computedData.volatilityValue ?? 0),
				minPerformance: Math.min(acc.minPerformance, row.computedData.performanceValue ?? 0),
			}),
			{
				maxVolatility: 0,
				maxPerformance: 0,
				minVolatility: 0,
				minPerformance: 0,
			},
		);
		return {
			maxVolatility: roundCustomByStep(value.maxVolatility, 0.5),
			maxPerformance: roundCustomByStep(value.maxPerformance, 0.5),
			minVolatility: roundCustomByStep(value.minVolatility, 0.5),
			minPerformance: roundCustomByStep(value.minPerformance, 0.5),
		};
	}, [compositionRows]);

	const graphLimits = category.kind === "composition" ? compositionGraphLimits : groupedGraphLimits;

	// Generate Markers
	const graphColumnMarkers = useMemo(
		() => ({
			markersVolatility: getGraphMarkers(graphLimits.maxVolatility, graphLimits.minVolatility, 0.5, "%", 10),
			markersPerformance: getGraphMarkers(graphLimits.maxPerformance, graphLimits.minPerformance, 0.5, "%", 10),
		}),
		[graphLimits.maxPerformance, graphLimits.maxVolatility, graphLimits.minPerformance, graphLimits.minVolatility],
	);

	// Get constribution translation Object Options
	const constributionT = useMemo(
		() =>
			t(`PORTFOLIO_CONTRIBUTION_VOLATILITY`, {
				returnObjects: true,
			}),
		[t],
	);
	// Shared renderers used by both the grouped table and the flat (composition) table.
	const renderPercentCell = useCallback(
		(value: number | null | undefined, cellProps: any, opts: { color: string; bold: boolean }) =>
			value == null ? (
				"N/A"
			) : (
				<TableDataCell {...cellProps}>
					<span className={opts.bold ? "font-semibold" : ""} style={{ color: opts.color }}>
						{`${formatNumber(value, 2)}%`}
					</span>
				</TableDataCell>
			),
		[formatNumber],
	);

	const renderGraphCell = useCallback(
		(
			value: number | null | undefined,
			cellProps: any,
			opts: { color: string; scale: { max: number; min: number } },
		) => {
			if (value == null) {
				return null;
			}
			return (
				<Row justifyContent="center" {...cellProps} childrenGrow={1} alignItems="center">
					<BarGraphPCSvg
						className="w-full"
						options={{
							resize: true,
							marksLabels: false,
							markerStep: 0.5,
							scale: opts.scale,
						}}
						data={[{ value, color: opts.color }]}
					/>
				</Row>
			);
		},
		[],
	);

	const renderGraphHeader = useCallback(
		(markers: Array<{ label: string }>) => (headerProps: any) => (
			<Row {...headerProps} justifyContent="space-between" alignItems="center">
				<ForEach collection={markers}>
					{(m) => (
						<Text as="div" type="Body/S/BOLD-UPPERCASE" color={themeCSSVars.palette_N500}>
							{m.label}
						</Text>
					)}
				</ForEach>
			</Row>
		),
		[],
	);

	type MetricRow = {
		computedData: {
			performanceValue: number | null;
			volatilityValue: number | null;
		};
	};

	const performanceColumn = useMemo(
		() => ({
			name: "performance" as const,
			header: "PORTFOLIO PERFORMANCE",
			cellClassName: "tabular-nums",
			sortFn(a: MetricRow, b: MetricRow) {
				return builtInSort(a.computedData.performanceValue, b.computedData.performanceValue);
			},
			width: 130,
			align: "end" as const,
		}),
		[],
	);

	const volatilityColumn = useMemo(
		() => ({
			name: "volatility" as const,
			header: "PORTFOLIO VOLATILITY",
			cellClassName: "tabular-nums",
			sortFn(a: MetricRow, b: MetricRow) {
				return builtInSort(a.computedData.volatilityValue, b.computedData.volatilityValue);
			},
			width: 130,
			align: "end" as const,
		}),
		[],
	);

	const performanceScale = useMemo(
		() => ({
			max: graphLimits.maxPerformance,
			min: graphLimits.minPerformance,
		}),
		[graphLimits.maxPerformance, graphLimits.minPerformance],
	);
	const volatilityScale = useMemo(
		() => ({ max: graphLimits.maxVolatility, min: graphLimits.minVolatility }),
		[graphLimits.maxVolatility, graphLimits.minVolatility],
	);

	const groupedColumns = useCallback<
		TableWithGroupsProps<
			NonNullable<typeof computedContribution>["drilldownData"][number] & {
				rows: NonNullable<typeof computedContribution>["drilldownData"][number]["drilldown"];
			}
		>["columns"]
	>(
		({ expandColumn }) => [
			expandColumn,
			{
				header:
					category.kind === "grouped"
						? constributionT.TABLE[category.selector as keyof typeof constributionT.TABLE]
						: "",
				groupContent: (groupedRow) => groupedRow.value as any,
				content: (row) => row.value as any,
				sortFn: builtInSortFnFor("value"),
				name: "value",
				minWidth: 200,
			},
			{
				...performanceColumn,
				groupCellClassName: "tabular-nums",
				groupContent: (groupedRow, cellProps) =>
					renderPercentCell(groupedRow.computedData.performanceValue, cellProps, {
						color: PERFORMANCE_COLOR,
						bold: true,
					}),
				content: (row, cellProps) =>
					renderPercentCell(row.computedData.performanceValue, cellProps, {
						color: PERFORMANCE_COLOR,
						bold: false,
					}),
			},
			{
				name: "performanceGraph",
				orderable: false,
				header: renderGraphHeader(graphColumnMarkers.markersPerformance),
				groupContent: ({ computedData: { performanceValue } }, cellProps) =>
					renderGraphCell(performanceValue, cellProps, {
						color: PERFORMANCE_COLOR,
						scale: performanceScale,
					}),
				content: ({ computedData: { performanceValue } }, cellProps) =>
					renderGraphCell(performanceValue, cellProps, {
						color: PERFORMANCE_COLOR,
						scale: performanceScale,
					}),
				minWidth: 300,
			},
			{
				...volatilityColumn,
				groupCellClassName: "tabular-nums",
				groupContent: (groupedRow, cellProps) =>
					renderPercentCell(groupedRow.computedData.volatilityValue, cellProps, {
						color: VOLATILITY_COLOR,
						bold: true,
					}),
				content: (row, cellProps) =>
					renderPercentCell(row.computedData.volatilityValue, cellProps, {
						color: VOLATILITY_COLOR,
						bold: true,
					}),
			},
			{
				name: "volatilityGraph",
				orderable: false,
				header: renderGraphHeader(graphColumnMarkers.markersVolatility),
				groupContent: ({ computedData: { volatilityValue } }, cellProps) =>
					renderGraphCell(volatilityValue, cellProps, {
						color: VOLATILITY_COLOR,
						scale: volatilityScale,
					}),
				content: ({ computedData: { volatilityValue } }, cellProps) =>
					renderGraphCell(volatilityValue, cellProps, {
						color: VOLATILITY_COLOR,
						scale: volatilityScale,
					}),
				minWidth: 300,
			},
		],
		[
			category,
			constributionT,
			graphColumnMarkers.markersPerformance,
			graphColumnMarkers.markersVolatility,
			performanceColumn,
			performanceScale,
			renderGraphCell,
			renderGraphHeader,
			renderPercentCell,
			volatilityColumn,
			volatilityScale,
		],
	);

	const compositionColumns = useMemo<Array<TableColumn<(typeof compositionRows)[number]>>>(
		() => [
			{
				header: "INSTRUMENT",
				content: (row) => row.value,
				sortFn: builtInSortFnFor("value"),
				name: "value",
				minWidth: 200,
			},
			{
				...performanceColumn,
				content: (row, cellProps) =>
					renderPercentCell(row.computedData.performanceValue, cellProps, {
						color: PERFORMANCE_COLOR,
						bold: false,
					}),
			},
			{
				name: "performanceGraph",
				orderable: false,
				header: renderGraphHeader(graphColumnMarkers.markersPerformance),
				content: ({ computedData: { performanceValue } }, cellProps) =>
					renderGraphCell(performanceValue, cellProps, {
						color: PERFORMANCE_COLOR,
						scale: performanceScale,
					}),
				minWidth: 300,
			},
			{
				...volatilityColumn,
				content: (row, cellProps) =>
					renderPercentCell(row.computedData.volatilityValue, cellProps, {
						color: VOLATILITY_COLOR,
						bold: true,
					}),
			},
			{
				name: "volatilityGraph",
				orderable: false,
				header: renderGraphHeader(graphColumnMarkers.markersVolatility),
				content: ({ computedData: { volatilityValue } }, cellProps) =>
					renderGraphCell(volatilityValue, cellProps, {
						color: VOLATILITY_COLOR,
						scale: volatilityScale,
					}),
				minWidth: 300,
			},
		],
		[
			graphColumnMarkers.markersPerformance,
			graphColumnMarkers.markersVolatility,
			performanceColumn,
			performanceScale,
			renderGraphCell,
			renderGraphHeader,
			renderPercentCell,
			volatilityColumn,
			volatilityScale,
		],
	);

	const graphLegend = (
		<GraphLegend>
			<div className="legend-container light book" style={{ marginRight: "1rem" }}>
				<ColoredRectangle color="#005C8B" variant="vertical" /> Portfolio performance
			</div>
			<div className="legend-container light book">
				<ColoredRectangle color="#7E45A9" variant="vertical" /> Portfolio volatility
			</div>
		</GraphLegend>
	);

	return (
		<div className="flex flex-col min-h-0">
			<ContributionVolatilityHeader
				category={category}
				setCategory={onChangeCategory}
				horizon={horizon}
				setHorizon={onChangeHorizon}
				significantOnly={significantOnly}
				setSignificantOnly={onChangeSignificantOnly}
			/>
			{category.kind === "composition" ? (
				compositionRows.length === 0 ? (
					(asOfMissingDataWall(ctx.asOf) ?? <IconWalls.DataNotAvailable className="mt-3" />)
				) : (
					<>
						<div className="w-full pb-3 mt-3 mb-2 px-2 flex flex-col min-h-0">
							<AutoSortTable
								rows={compositionRows}
								rowKey={(r) => r.index}
								columns={compositionColumns}
								orderBy={orderBy}
								onOrderByChange={onOrderByChange}
							/>
						</div>
						{graphLegend}
					</>
				)
			) : !computedContribution?.drilldownData.length ? (
				(asOfMissingDataWall(ctx.asOf) ?? <IconWalls.DataNotAvailable className="mt-3" />)
			) : (
				<>
					<div className="w-full pb-3 mt-3 mb-2 px-2 flex flex-col min-h-0">
						<AutoSortTableWithGroups
							groupedRows={computedContribution.drilldownData.map((el) => ({
								...el,
								rows: el.drilldown,
							}))}
							groupRowKey={(r) => r.index}
							columns={groupedColumns}
							orderBy={orderBy}
							onOrderByChange={onOrderByChange}
						/>
					</div>
					{graphLegend}
				</>
			)}
		</div>
	);
};

export default withContext(PortfolioContext)(ContributionVolatility);

function ContributionVolatilityHeader(props: {
	category: CategorySelection;
	setCategory: Setter<CategorySelection>;
	horizon: ReportHorizon;
	setHorizon: Setter<ReportHorizon>;
	significantOnly: boolean;
	setSignificantOnly?: Setter<boolean>;
}) {
	return (
		<div className="flex justify-between">
			<div className="inline-block max-w-1/6">
				<Select value={props.category} onChange={props.setCategory} options={categorySelectOptions} />
			</div>
			<Row gap={4} alignItems="center" className="max-w-1/6">
				<Switch
					checked={props.significantOnly}
					onChange={props.setSignificantOnly}
					disabled={props.setSignificantOnly == null}
				>
					Significant values only
				</Switch>
				<Select value={props.horizon} onChange={props.setHorizon} options={horizonOptions} />
			</Row>
		</div>
	);
}
